import { Injectable } from '@angular/core';
// import { timer } from 'rxjs';


import { FirestorePathsService } from './firestorePaths.service';
import { FirestoreService } from './firestore.service';
import { PickupRequestsService } from './pickupRequests.service';
import { PickupResponsesService } from './pickupResponses.service';
import { ShuttlesService } from './shuttles.service';
import { UtilsService } from './utils.service';
import type { TPickupRequest, IPickupRequest, TVector, TShuttle, TTicket } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class PickupQueueService {
    private pickupResponsesSubscription: any;
    // private shuttles: TShuttle[];
    private pickupRequest: TPickupRequest;
    private queuePromise = null;        // Processing of the tickets in the queue is done every quarter second interval
    private queryablePromise: any;

    constructor(private firestoreService: FirestoreService, private firestorePathsService: FirestorePathsService,
        private shuttlesService: ShuttlesService,
        private utilsService: UtilsService,
        private pickupResponsesService: PickupResponsesService,
        private pickupRequestsService: PickupRequestsService) {
    }

    // public getByUid(uid: string) {
    //     const collection = this.firestorePathsService.getUserAccountMapsCol();
    //     return this.firestoreService.getByUid(this.collection, uid);
    // }

    // public getAll(): any {
    //     return this.firestoreService.getAll(this.collection);
    // }

    // public add(req: TPickupRequest) {
    //     return this.firestoreService.add(this.collection, req);
    // }

    // public update(req: TPickupRequest): any {
    //     return this.firestoreService.update(this.collection, req);
    // }

    // public delete(uid: string): any {
    //     return this.firestoreService.delete(this.collection, uid);
    // }

    public async initialize() {
        // this.shuttles = await this.shuttlesService.getAll();

        //  DO NOT DELETE THIS CODE.
        //  Right now the PickupRequestsService is listening for new tickets.  That means all the service advisors
        //  computers will have their PickupRequestsService listening for new tickets.  Assume frontier toyota
        //  has 10 service advisors.  That means all 10 computers will be listening for newly created tickets
        //  and sending out 10 pickup requests to the same driver.  Not good.  The alternative is to have
        //  the service advisors's computer explicitly call the pickup request code.  This way only one computer
        //  will be busy doing the pickup request routines.
        //
        //  The sendPickupRequestToClosestDrivers() is now explicitly called by TicketController.
        //  Make sure we remove all previous listeners.
        //  Keep this code here for debugging purposes only.
    }

    public localAvailableShuttles(shuttles) {
        let title;
        let msg;

        if (shuttles.length > 0) {
            const shuttleNames = shuttles.map((item) => item.shuttleName);
            title = 'Good News!';
            msg = 'Sending pickup request to: ' + shuttleNames.join(', ');  // Shuttle 1, Shuttle 3
        } else {
            title = 'Bad News :(';
            msg = 'All shuttles are at the dealership right now.';
        }
        // console.log(title, msg);
    }

    // Create a pickup request from the incoming ticket if
    // 1) The ticket is going to the dealership - which means a pickup at a location other than the dealership.
    // 2) The ticket is pending
    // public addTicketToPickupQueue(ticket, availableShuttlesCallback?) {
    public addTicketToPickupQueue(ticket: TTicket) {
        if (ticket.fromDealership === false && ticket.status === 'pending') {
            this.pickupRequest = {
                'ticket': ticket, 'promises': [], 'closestShuttles': [], 'status': 'findShuttles', 'responses': [],
                'currentIdx': 0, 'waitingCount': 0
            };

            // We are about to send a request to a shuttle asking if he could pickup this ticket.
            // The shuttle out driving (NavigationPage4) will write his response to the pickupResponses node.
            if (this.pickupResponsesSubscription) {
                this.pickupResponsesSubscription.unsubscribe();
            }
            const observable = this.pickupResponsesService.getObservable();
            this.pickupResponsesSubscription = observable.subscribe(async (responses) => {
                if (responses && responses.length > 0) {
                    await this.shuttleResponseReceived(responses);
                }
            });

            // Start executing the tasks one by one for this pickup request
            if (this.queuePromise === null) {
                this.queuePromise = setInterval(async () => {
                    await this.processPickupRequestTasks();
                }, 1000);
            }
        }
    }

    // findShuttles       = find closest shuttles
    // sendRequest        = send request to shuttle1, if shuttle1 declined pickup then send request to shuttle2
    // waitForResponse    = wait for a response from the current shuttle
    // responseReceived   = shuttle responded
    private async processPickupRequestTasks(): Promise<void> {
        if (!this.pickupRequest) {
            await clearInterval(this.queuePromise);
            this.queuePromise = null;
            return;
        }

        // Only process the next task if the current tasks promise was finished processing.
        if (this.queryablePromise) {
            if (this.queryablePromise.isFulfilled()) {
                // The current task finished successfully.  Process the next task.
                this.queryablePromise = null;
            } else if (this.queryablePromise.isPending()) {
                // The current task hasn't started to execute yet or is still executing.
                return;
            } else if (this.queryablePromise.isRejected()) {
                await clearInterval(this.queuePromise);
                this.queuePromise = null;
                return;
            }
        }

        // this.queryablePromise = null;
        let promise: any;
        switch (this.pickupRequest.status) {
            case 'findShuttles':
                // Find and sort shuttles on how far they are from this ticket's pickup location
                promise = this.findShuttles(this.pickupRequest);
                break;

            case 'sendRequest':
                // Send request to shuttle N
                promise = this.sendRequest(this.pickupRequest);
                break;

            case 'waitAndSupplyDefaultDeclinedResponse':
                // Waiting for a response from shuttle N
                promise = this.waitAndSupplyDefaultDeclinedResponse(this.pickupRequest);
                break;

            case 'quit':
                this.pickupRequest = null;
                break;
        }

        // Turn the promise into a queryable promise.
        // #commented-out-function-call
        // if (promise) {
        //     this.queryablePromise = this.utilsService.MakeQuerablePromise(promise);
        // }
    }

    // Step 1
    private async findShuttles(pr: IPickupRequest): Promise<TPickupRequest> {
        pr.closestShuttles = await this.findClosestShuttlesToTicket(pr.ticket);
        // pr.availableShuttlesCallback(pr.closestShuttles);
        if (pr.closestShuttles.length > 0) {
            pr.status = 'sendRequest';
        } else {
            // All shuttles are at the dealership.  Which means no shuttle is out driving to
            // send a request to.  So remove this pickup request from the queue.
            // this.pickupRequestQueue.splice(this.currentQueueIdx, 1);
            pr.status = 'quit';
        }
        return pr;
    }

    // Step 2 - Send a pickup request to the next shuttle.
    private async sendRequest(pr: IPickupRequest): Promise<TPickupRequest> {
        if (pr.responses.length === pr.closestShuttles.length) {
            // We asked every shuttle to accept this pickup and they all turned it down.
            // Stop processing this request.
            pr.status = 'quit';
        } else {
            // If two shuttles already declined this pickup request, then ask the third shuttle.
            const numResponses = pr.responses.length;
            const shuttle = pr.closestShuttles[numResponses];
            pr.waitingCount = 0;
            pr.status = 'waitAndSupplyDefaultDeclinedResponse';

            // Each shuttle listens for requests in the pickupRequests node.
            // So pushing request in this node will popup a dialog on the shuttle drivers phone.
            const request = { 'shuttleUid': shuttle.uid, 'ticket': pr.ticket, 'uid': shuttle.uid };
            await this.pickupRequestsService.delete(shuttle.uid);
            await this.pickupRequestsService.set(request);
        }
        return pr;
    }

    // Step 3 - Wait for a response from shuttle N.  If no response comes
    // after 12 seconds then supply a declined response.
    private async waitAndSupplyDefaultDeclinedResponse(pr: IPickupRequest): Promise<TPickupRequest> {
        if (++pr.waitingCount === 12) {
            // This shuttle driver didn't even respond - how rude!  Supply a declined response.
            const numResponses = pr.responses.length;
            const shuttle = pr.closestShuttles[numResponses];
            const shuttleUid = shuttle.uid;
            const defaultDeclinedResponse: any = {
                'shuttleUid': shuttleUid, 'ticketUid': pr.ticket.uid, 'ticket': pr.ticket,
                'accepted': false
            };

            pr.responses.push(defaultDeclinedResponse);
            pr.status = 'responseReceived';
            await this.pickupResponsesService.add(defaultDeclinedResponse);
        }
        return pr;
    }

    // This routine is invoked when:
    // 1) The shuttle driver responds to the pickup request.
    // 2) The shuttle driver doesn't respond and the local method: waitAndSupplyDefaultDeclinedResponse
    //    is forced to supply a value.
    // All this routine needs to do is add this response to the appropriate pickup request.
    private async shuttleResponseReceived(responses) {
        if (!this.pickupRequest) {
            return;
        }

        // The incoming responses array contains all the responses in the node.
        // It's possible that another service advisor is requesting a shuttle for the person they are helping.
        // The only response we are interested in, is the one that contains our ticket.
        const response = responses.find((resp) => (resp.ticket.uid === this.pickupRequest.ticket.uid));
        if (!response) {
            return;
        }

        const shuttleUid = response.shuttleUid;
        // The driver either accepted the pickup request or not.  If accepted is false
        // (the driver for whatever reason can't do the pickup) then delete the request
        // and ask the next driver/shuttle to accept the pickup.
        //
        // On the other hand, if the driver did accept the pickup request then the NavigationPage4
        // added the ticket into his route.  This means we still don't need this request
        // anymore.  So in both cases we don't need the request anymore.
        await this.deleteOldRequestsAndResponses(shuttleUid);

        if (response.accepted) {
            // Also remove this request from our queue.
            // Use the ticket uid to find the index that corresponds with this response.
            // const idx = this.pickupRequestQueue.findIndex((element) => {
            //     return (element.ticket.uid === response.ticket.uid);
            // });

            // this.pickupRequestQueue.splice(idx, 1);
            // this.pickupRequest = null;
            this.pickupRequest.status = 'quit';
        } else {
            // This shuttle driver didn't accept this ticket, so ask the next shuttle driver.
            this.pickupRequest.status = 'sendRequest';
        }
    }

    // #region Helper Functions

    // Return a promise that contains an array of the closest shuttles
    private async findClosestShuttlesToTicket(ticket: TTicket): Promise<any> {
        const shuttles: TShuttle[] = await this.shuttlesService.getByFieldValue('status', 'Traveling');
        const vectors: TVector[] = await this.shuttlesService.getVectors();

        // // Creates a deep copy - a totally different and distinct object from shuttles.
        // // const shuttleVectorsOutDriving = JSON.parse(JSON.stringify(vectors));
        // const shuttleVectorsOutDriving = this.utilsService.deepClone(vectors);

        // // Compute the distance from the shuttle to the pickup location.
        const closestShuttleVectors = [];
        vectors.forEach((vector) => {
            const distance = this.utilsService.calculateDistance(vector.currentLocation, ticket.pickup.address.coordinate);
            vector.distanceToTicket = distance;
            closestShuttleVectors.push(vector);
        });

        // Sort this array by the distance to the ticket.
        closestShuttleVectors.sort((vector1, vector2) => (vector1.distanceToTicket.miles - vector2.distanceToTicket.miles));

        return (closestShuttleVectors);
    }

    // Delete all requests and responses older than 3 minutes.
    private async deleteOldRequestsAndResponses(shuttleUid: string) {
        const count1 = await this.pickupRequestsService.deleteOld(shuttleUid, 3 * 60 * 1000);
        const count2 = await this.pickupResponsesService.deleteOld(shuttleUid, 3 * 60 * 1000);
        return { 'requestsCount': count1, 'responsesCount': count2 };
    }
}
// #endregion
